/**
 * StreamBanner — inline banner inside BuilderStreamTerminal for SDK error
 * and rate-limit events.
 *
 * Surfaces ``cc: "error"`` (severity: warning / error / fatal) and
 * ``cc: "rate_limit"`` events emitted by ``stream_runner.py`` so the user
 * sees stalls / failures without digging through CodeBuild logs.
 *
 * Severity → palette mapping:
 *   - rate_limit            → amber (Clock icon, "Rate limit")
 *   - severity=warning      → amber (AlertTriangle, "Warning")
 *   - severity=fatal        → red   (ShieldAlert, "Fatal")
 *   - severity=error / *    → red   (ShieldAlert, "Error")
 *
 * Optional fields:
 *   - detail | errorType    rendered as monospace tag next to label
 *   - retryAfter | resetsAt right-aligned "retry after <n>s"
 *   - message | text        body text (whitespace-preserving)
 */
import React from 'react';
import { AlertTriangle, ShieldAlert, Clock } from 'lucide-react';

export default function StreamBanner({ evt }) {
  const isRateLimit = evt.cc === 'rate_limit';
  const severity = (evt.severity || (isRateLimit ? 'warning' : 'error')).toLowerCase();
  const message = evt.message || evt.text || (isRateLimit ? 'Rate limit reached.' : 'An error occurred.');
  const detail = evt.detail || evt.errorType || null;
  const retryAfter = evt.retryAfter || evt.resetsAt || null;

  let palette;
  let Icon;
  let label;
  if (isRateLimit) {
    palette = 'border-amber-500/40 bg-amber-950/30 text-amber-100';
    Icon = Clock;
    label = 'Rate limit';
  } else if (severity === 'warning') {
    palette = 'border-amber-500/40 bg-amber-950/30 text-amber-100';
    Icon = AlertTriangle;
    label = 'Warning';
  } else {
    palette = 'border-red-500/40 bg-red-950/30 text-red-100';
    Icon = ShieldAlert;
    label = severity === 'fatal' ? 'Fatal' : 'Error';
  }

  return (
    <div className={`my-1.5 rounded-md border px-2.5 py-1.5 text-[10px] ${palette}`} role="alert">
      <div className="flex items-start gap-1.5">
        <Icon className="h-3 w-3 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold uppercase tracking-wider text-[9px]">{label}</span>
            {detail && <span className="text-slate-300/70 font-mono">{detail}</span>}
            {retryAfter && (
              <span className="ml-auto text-slate-300/70 font-mono">
                retry after {typeof retryAfter === 'number' ? `${retryAfter}s` : retryAfter}
              </span>
            )}
          </div>
          <div className="mt-0.5 whitespace-pre-wrap break-words leading-relaxed">{message}</div>
        </div>
      </div>
    </div>
  );
}
